import React, { useEffect, useState } from 'react';
import Select, { components, Props as SelectProps } from 'react-select';
import { SortableContainer, SortableElement, SortableHandle } from 'react-sortable-hoc';
import { select2Config } from '../config';

interface SelectTwoProps {
    name?: string;
    id?: string;
    onFocus?: (value: any) => void;
    inputValue?: any;
    options: SelectTwoOption[];
    onChange?: (value: any) => void;
    placeholder?: string;
    hasError?: boolean;
    isMulti?: boolean;
    disabled?: boolean;
    size?: string;
    isSortable?: boolean;
    isClearable?: boolean;
    /** Renderiza el menú en document.body para evitar que se corte dentro de modales */
    menuPortalTarget?: HTMLElement | null;
}

export interface SelectTwoOption {
    value: string | number | null;
    label: string;
}

export const SelectTwoMapperOptions = (
    options: any[],
    valueName = 'id',
    labelNames = ['name'],
    LabeLSeparation = ' | '
) => {
    if (!options) return [];

    return options.map((option: any) => {
        const labels: string[] = [];
        labelNames.map((labelName: string) => {
            labels.push(option[labelName]);
        });
        return {
            value: option[valueName],
            label: labels.join(LabeLSeparation)
        };
    });
};

const SortableMultiValueLabel = SortableHandle((props: any) => (
    <components.MultiValueLabel {...props} />
));

const SortableMultiValue = SortableElement((props: any) => (
    <components.MultiValue
        {...props}
        innerProps={{
            ...props.innerProps,
            onMouseDown: (e) => {
                e.preventDefault();
                e.stopPropagation();
            }
        }}
    />
));

const SortableSelect = SortableContainer(Select);
const selectedOption = (options: SelectTwoOption[], value: string | number | null) => {
    return options.find((option: SelectTwoOption) => option.value == value) || null;
};
const SelectTwo: React.FC<SelectTwoProps> = ({
    name,
    id,
    onFocus,
    inputValue,
    options,
    onChange,
    placeholder = 'Seleccione una opción',
    hasError = false,
    isMulti = false, // Cambiar a true porque este componente es multi-select
    disabled,
    isSortable = false,
    size = 'medium',
    isClearable = false,
    menuPortalTarget
}) => {
    function arrayMove<T>(array: readonly T[], from: number, to: number) {
        const slicedArray = array.slice();
        slicedArray.splice(to < 0 ? array.length + to : to, 0, slicedArray.splice(from, 1)[0]);
        return slicedArray;
    }
    const onSortEnd = ({ oldIndex, newIndex }: { oldIndex: number; newIndex: number }) => {
        const newValue = arrayMove(inputValue, oldIndex, newIndex);
        if (typeof onChange === 'function') {
            onChange(newValue);
        }
    };

    const sortableProps: any = {
        useDragHandle: true,
        axis: 'xy',
        onSortEnd: onSortEnd,
        distance: 4
    };

    const sortableComponents = isSortable
        ? {
              MultiValue: SortableMultiValue,
              MultiValueLabel: SortableMultiValueLabel
          }
        : {};

    const portalTarget = menuPortalTarget ?? (typeof document !== 'undefined' ? document.body : null);

    const baseStyles = select2Config(hasError, size);
    const styles = portalTarget
        ? {
              ...baseStyles,
              menuPortal: (provided: any) => ({ ...provided, zIndex: 9999 }),
              menu: (provided: any) => ({ ...provided, zIndex: 9999 })
          }
        : baseStyles;

    return (
        <SortableSelect
            {...sortableProps}
            id={id}
            isMulti={isMulti}
            options={options}
            value={Array.isArray(inputValue) ? inputValue : selectedOption(options, inputValue)}
            onChange={onChange}
            components={sortableComponents}
            placeholder={placeholder}
            isDisabled={disabled}
            name={name}
            styles={styles}
            isClearable={isClearable}
            onFocus={onFocus}
            menuPortalTarget={portalTarget}
            menuPosition={portalTarget ? 'fixed' : 'absolute'}
        />
    );
};

export default SelectTwo;
